import React, { useState } from 'react';

// Time-range pill toggle for the Cost Dashboard. 7d / 30d / 90d / Custom.
// Custom opens a tiny inline date picker. Selection is owned by the parent
// (CostDashboard) and persisted to cost_dashboard_prefs.

const PRESETS = [
  { key: '7d',  label: '7d' },
  { key: '30d', label: '30d' },
  { key: '90d', label: '90d' },
  { key: 'custom', label: 'Custom' },
];

export default function TimeRangeControl({
  value,
  onChange,
  customStart,
  customEnd,
  onCustomChange,
}) {
  const [pickerOpen, setPickerOpen] = useState(false);

  const handleClick = (key) => {
    if (key === 'custom') {
      setPickerOpen(p => !p);
      // Don't switch the active range until both dates are set — opening
      // the picker is a separate intent from committing a custom window.
      return;
    }
    setPickerOpen(false);
    onChange(key);
  };

  const handleCustomCommit = () => {
    if (!customStart || !customEnd) return;
    if (customStart > customEnd) return;
    onChange('custom');
    setPickerOpen(false);
  };

  return (
    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <div
        role="tablist"
        aria-label="Time range"
        style={{
          display: 'inline-flex',
          background: 'var(--bg-subtle)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: 2,
        }}
      >
        {PRESETS.map(p => {
          const active = value === p.key || (p.key === 'custom' && value === 'custom');
          return (
            <button
              key={p.key}
              role="tab"
              aria-selected={active}
              onClick={() => handleClick(p.key)}
              style={{
                padding: '5px 12px',
                fontSize: 12,
                fontWeight: 600,
                border: 'none',
                background: active ? 'var(--bg-card)' : 'transparent',
                color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
                borderRadius: 6,
                cursor: 'pointer',
                boxShadow: active ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
                transition: 'background 120ms ease, color 120ms ease',
              }}
            >
              {p.label}
            </button>
          );
        })}
      </div>

      {pickerOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: 6,
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            padding: 12,
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            zIndex: 30,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            minWidth: 240,
          }}
        >
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Start
            <input
              type="date"
              value={customStart || ''}
              onChange={(e) => onCustomChange({ start: e.target.value, end: customEnd })}
              style={{
                display: 'block', marginTop: 4, width: '100%',
                background: 'var(--bg-subtle)', border: '1px solid var(--border)',
                borderRadius: 6, padding: '6px 8px', fontSize: 13,
                color: 'var(--text-primary)',
              }}
            />
          </label>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            End
            <input
              type="date"
              value={customEnd || ''}
              onChange={(e) => onCustomChange({ start: customStart, end: e.target.value })}
              style={{
                display: 'block', marginTop: 4, width: '100%',
                background: 'var(--bg-subtle)', border: '1px solid var(--border)',
                borderRadius: 6, padding: '6px 8px', fontSize: 13,
                color: 'var(--text-primary)',
              }}
            />
          </label>
          {customStart && customEnd && customStart > customEnd && (
            <div style={{ fontSize: 11, color: 'var(--text-critical)' }}>
              Start must be before end
            </div>
          )}
          <button
            onClick={handleCustomCommit}
            disabled={!customStart || !customEnd || customStart > customEnd}
            style={{
              padding: '6px 10px',
              background: 'var(--text-primary)',
              color: 'var(--bg-card)',
              border: 'none',
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              opacity: (!customStart || !customEnd || customStart > customEnd) ? 0.5 : 1,
            }}
          >
            Apply
          </button>
        </div>
      )}
    </div>
  );
}
